import { useLocation, useNavigate } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { snapshotMainScroll } from '@/lib/mainScrollRestore';
import { OverflowPills } from '@/components/dashboard/OverflowPills';
import {
  expandGoals,
  type OnboardingGoal,
} from '@/components/onboarding-v2/onboardingV2Model';

// Workspace cards use the onboarding illustrations (same set as step 1).
const inboxIcon = '/onboardingimg/inboxicon.png';
const campaignsIcon = '/onboardingimg/campaign.png';
const automationsIcon = '/onboardingimg/automation.png';
const crmIcon = '/onboardingimg/CRMicon.png';
const adsIcon = '/assets/social-media/meta-icon.svg';
const socialIcon = '/onboardingimg/socialmediascheduling.svg';

/**
 * "What's inside your workspace" feature grid.
 *
 * Card chrome matches SetupStepCard (green border, icon+title row, capability
 * chips, full-width primary CTA) so the plan page reads as one system.
 *
 * There is no "All features" / "More features" recap card — leftover step-1
 * products (Meta Ads, Social) are real cards, same as Inbox / Campaigns.
 */

interface Feature {
  key: string;
  name: string;
  icon: string;
  features: readonly string[];
  ctaLabel: string;
  route: string;
}

const FEATURES: Feature[] = [
  {
    key: 'channels',
    name: 'Unified Inbox',
    icon: inboxIcon,
    features: ['WhatsApp', 'Instagram', 'Facebook'],
    ctaLabel: 'Open inbox',
    route: '/home/channels/whatsapp',
  },
  {
    key: 'campaigns',
    name: 'Campaigns',
    icon: campaignsIcon,
    features: ['Broadcasts', 'Templates', 'Delivery tracking'],
    ctaLabel: 'Open campaigns',
    route: '/home/campaigns',
  },
  {
    key: 'automations',
    name: 'Automations',
    icon: automationsIcon,
    features: ['Auto-reply', 'Flow builder', 'Nexus AI'],
    ctaLabel: 'Open automations',
    route: '/home/automation',
  },
  {
    key: 'ads',
    name: 'Meta Ads',
    icon: adsIcon,
    features: ['Ad campaigns', 'Audiences', 'Insights'],
    ctaLabel: 'Open Meta Ads',
    route: '/home/ads',
  },
  {
    key: 'social',
    name: 'Social scheduling',
    icon: socialIcon,
    features: ['Posts scheduling', 'Instagram', 'Facebook'],
    ctaLabel: 'Open social',
    route: '/home/social-media/posts',
  },
  {
    key: 'crm',
    name: 'CRM',
    icon: crmIcon,
    features: ['Pipeline', 'Contacts', 'Tickets'],
    ctaLabel: 'Open CRM',
    route: '/home/boards/leads',
  },
];

/**
 * Step-1 goals already shown in Setup Progress → workspace cards to hide so
 * section 2 does not repeat them. Inbox / CRM have no step-1 twin.
 */
const WORKSPACE_KEYS_FOR_GOAL: Partial<Record<OnboardingGoal, readonly string[]>> = {
  'whatsapp-automation': ['automations'],
  'instagram-automation': ['automations'],
  'campaigns-broadcasts': ['campaigns'],
  'meta-ads': ['ads'],
  'social-scheduling': ['social'],
};

export function hiddenWorkspaceKeysForGoals(goals: readonly OnboardingGoal[]): Set<string> {
  const keys = new Set<string>();
  for (const goal of expandGoals(goals)) {
    for (const key of WORKSPACE_KEYS_FOR_GOAL[goal] ?? []) keys.add(key);
  }
  return keys;
}

/** Visible card keys after hiding anything already on the plan. */
export function visibleWorkspaceFeatureKeys(
  selected: readonly OnboardingGoal[] = []
): string[] {
  const hidden = hiddenWorkspaceKeysForGoals(selected);
  return FEATURES.filter((f) => !hidden.has(f.key)).map((f) => f.key);
}

export function WorkspaceFeatures({
  excludeGoals,
}: {
  /** Goals already listed in Setup Progress (expanded step-1 selection). */
  excludeGoals?: readonly OnboardingGoal[];
} = {}) {
  const navigate = useNavigate();
  const location = useLocation();
  const hidden = hiddenWorkspaceKeysForGoals(excludeGoals ?? []);
  const visible = FEATURES.filter((f) => !hidden.has(f.key));

  if (visible.length === 0) return null;

  return (
    <div>
      <div className="mb-4 flex items-baseline justify-between">
        <h3 className="text-[18px] font-semibold text-[#101828]">
          What&apos;s inside your workspace
        </h3>
      </div>
      <div className="grid gap-3 sm:grid-cols-3">
        {visible.map((f) => (
          <article
            key={f.key}
            className="flex h-full min-w-0 flex-col rounded-2xl bg-white p-5 shadow-[0_2px_8px_rgba(16,24,40,0.04)]"
          >
            <div className="flex items-start gap-3.5">
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-[#F2F4F7] text-[#475467]">
                <img src={f.icon} alt="" className="h-6 w-6 object-contain" />
              </span>
              <div className="min-w-0 flex-1">
                <h3 className="text-[15px] font-semibold text-[#101828]">{f.name}</h3>
                <div className="mt-2 min-w-0">
                  <OverflowPills items={f.features} label={`${f.name} capabilities`} />
                </div>
              </div>
            </div>

            <div className="mt-auto grid grid-cols-1 gap-2 pt-4">
              <Button
                type="button"
                onClick={() => {
                  snapshotMainScroll(location.pathname, location.search);
                  navigate(f.route);
                }}
                variant="outline"
                className="!h-11 w-full justify-center gap-1.5 !rounded-xl !border-[#EEF8F3] !bg-[#EEF8F3] px-3 !text-[14px] font-semibold !text-[#0BAB5F] shadow-none hover:!border-transparent hover:!text-white hover:brightness-110 hover:[background:var(--main-color)] [&_svg]:!size-3.5"
              >
                {f.ctaLabel}
                <ArrowRight className="h-3.5 w-3.5" />
              </Button>
            </div>
          </article>
        ))}
      </div>
    </div>
  );
}
